import React from 'react';
import { 
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { OnboardingFilterState } from '../hooks/useOnboardingFilter';

interface OnboardingFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  tempState: Omit<OnboardingFilterState, 'activeFilters'>;
  expandedAccordions: {[key: string]: boolean};
  setExpandedAccordions: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  handleUsernameChange: (value: string) => void;
  handleFullNameChange: (value: string) => void;
  handleOnboardDateFromChange: (value: string) => void;
  handleOnboardDateToChange: (value: string) => void;
  handleEmailSentDateFromChange: (value: string) => void;
  handleEmailSentDateToChange: (value: string) => void;
  applyFilters: () => void;
  clearFilters: () => void;
}

const OnboardingFilterDrawer: React.FC<OnboardingFilterDrawerProps> = ({
  open,
  onClose,
  tempState,
  expandedAccordions,
  setExpandedAccordions,
  handleUsernameChange,
  handleFullNameChange,
  handleOnboardDateFromChange,
  handleOnboardDateToChange,
  handleEmailSentDateFromChange,
  handleEmailSentDateToChange,
  applyFilters,
  clearFilters
}) => {
  const theme = useTheme();
  
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 340 },
          maxWidth: '100%',
          borderTopLeftRadius: { xs: 0, sm: 16 },
          borderBottomLeftRadius: { xs: 0, sm: 16 },
          boxShadow: '0 0 20px rgba(0,0,0,0.15)'
        }
      }}
    >
      <Box 
        sx={{ 
          p: 2.5,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={2}
          pb={2}
          borderBottom={`1px solid ${alpha(theme.palette.divider, 0.7)}`}
        >
          <Box display="flex" alignItems="center">
            <FilterAltIcon 
              color="primary" 
              sx={{ 
                mr: 1.5, 
                fontSize: '1.5rem' 
              }} 
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main
              }}
            >
              Filter Onboarding Users
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                color: theme.palette.error.main
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Accordion 
            expanded={expandedAccordions.username}
            onChange={() => setExpandedAccordions(prev => ({
              ...prev, 
              username: !prev.username
            }))}
            defaultExpanded={true}
            elevation={0}
            sx={{
              mb: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRadius: '10px !important',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }
            }}
          >
            <AccordionSummary 
              expandIcon={
                <ExpandMoreIcon 
                  sx={{
                    color: expandedAccordions.username ? theme.palette.primary.main : 'inherit',
                    transition: 'all 0.3s'
                  }}
                />
              }
              sx={{
                backgroundColor: expandedAccordions.username ? alpha(theme.palette.primary.main, 0.04) : 'transparent'
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: expandedAccordions.username ? theme.palette.primary.main : 'inherit'
                }}
              >
                Username
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by username"
                value={tempState.username}
                onChange={(e) => handleUsernameChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    }
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>
          
          <Accordion 
            expanded={expandedAccordions.fullName}
            onChange={() => setExpandedAccordions(prev => ({
              ...prev, 
              fullName: !prev.fullName
            }))}
            defaultExpanded={true}
            elevation={0}
            sx={{
              mb: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRadius: '10px !important',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }
            }}
          >
            <AccordionSummary 
              expandIcon={
                <ExpandMoreIcon 
                  sx={{
                    color: expandedAccordions.fullName ? theme.palette.primary.main : 'inherit',
                    transition: 'all 0.3s'
                  }}
                />
              }
              sx={{
                backgroundColor: expandedAccordions.fullName ? alpha(theme.palette.primary.main, 0.04) : 'transparent'
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: expandedAccordions.fullName ? theme.palette.primary.main : 'inherit'
                }}
              >
                Full Name
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by full name"
                value={tempState.fullName}
                onChange={(e) => handleFullNameChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    }
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>
          
          <Accordion 
            expanded={expandedAccordions.onboardDate}
            onChange={() => setExpandedAccordions(prev => ({
              ...prev, 
              onboardDate: !prev.onboardDate
            }))}
            defaultExpanded={true}
            elevation={0}
            sx={{
              mb: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRadius: '10px !important',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }
            }}
          >
            <AccordionSummary 
              expandIcon={
                <ExpandMoreIcon 
                  sx={{
                    color: expandedAccordions.onboardDate ? theme.palette.primary.main : 'inherit',
                    transition: 'all 0.3s'
                  }}
                />
              }
              sx={{
                backgroundColor: expandedAccordions.onboardDate ? alpha(theme.palette.primary.main, 0.04) : 'transparent'
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: expandedAccordions.onboardDate ? theme.palette.primary.main : 'inherit'
                }}
              >
                Onboard Date
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={tempState.onboardDateFrom}
                  onChange={(e) => handleOnboardDateFromChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={tempState.onboardDateTo}
                  onChange={(e) => handleOnboardDateToChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      }
                    }
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
          
          <Accordion 
            expanded={expandedAccordions.emailSentDate}
            onChange={() => setExpandedAccordions(prev => ({
              ...prev, 
              emailSentDate: !prev.emailSentDate
            }))}
            defaultExpanded={true}
            elevation={0}
            sx={{
              mb: 1.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              borderRadius: '10px !important',
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                borderRadius: '10px',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                }
              }
            }}
          >
            <AccordionSummary 
              expandIcon={
                <ExpandMoreIcon 
                  sx={{
                    color: expandedAccordions.emailSentDate ? theme.palette.primary.main : 'inherit',
                    transition: 'all 0.3s'
                  }}
                />
              }
              sx={{
                backgroundColor: expandedAccordions.emailSentDate ? alpha(theme.palette.primary.main, 0.04) : 'transparent'
              }}
            >
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  color: expandedAccordions.emailSentDate ? theme.palette.primary.main : 'inherit'
                }}
              >
                Last Email Sent
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={tempState.emailSentDateFrom}
                  onChange={(e) => handleEmailSentDateFromChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={tempState.emailSentDateTo}
                  onChange={(e) => handleEmailSentDateToChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      }
                    }
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      
        <Box mt="auto">
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            onClick={applyFilters}
            startIcon={<FilterAltIcon />}
            sx={{ 
              py: 1,
              mb: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 15px rgba(0,0,0,0.15)'
              }
            }}
          >
            Apply Filters
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            fullWidth 
            onClick={clearFilters} 
            startIcon={<CleaningServicesIcon />}
            sx={{ 
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.08)
              }
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default OnboardingFilterDrawer;
